import React, { useState, useEffect, useRef } from "react";
import { Popover, Drawer } from "antd";
import { ClockCircleOutlined, CloseCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import useIsMobile from "./useIsMobile";

// Shared time picker. Value in/out is a plain "HH:mm" string (same as a native time input),
// so it drops into Form.Item without any conversion.
// Three scroll wheels (hour / minute / AM-PM) that apply live as they settle - no OK button.
// Closes on outside tap / Escape (popover on desktop, bottom sheet on phones).

const ITEM_H = 40;
const VISIBLE = 5;
const PAD = ITEM_H * ((VISIBLE - 1) / 2);

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const parse = (value) => {
  const m = typeof value === "string" && value.match(/^(\d{2}):(\d{2})/);
  if (!m) return null;
  const h24 = Number(m[1]);
  return { h12: h24 % 12 || 12, minute: Number(m[2]), period: h24 < 12 ? "AM" : "PM" };
};

const to24 = (h12, minute, period) => {
  const h = (h12 % 12) + (period === "PM" ? 12 : 0);
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const formatTime = (value, isArabic, labels) => {
  const p = parse(value);
  if (!p) return "";
  const locale = "en-US"; // times use Western digits in both languages (Saudi convention)
  const num = new Intl.NumberFormat(locale, { useGrouping: false });
  const two = new Intl.NumberFormat(locale, { minimumIntegerDigits: 2, useGrouping: false });
  return `${num.format(p.h12)}:${two.format(p.minute)} ${p.period === "AM" ? labels.am : labels.pm}`;
};

const Wheel = ({ items, selected, onSelect, width, isDisabled }) => {
  const ref = useRef(null);
  const timer = useRef(null);
  const selectedIndex = Math.max(0, items.findIndex((i) => i.value === selected));

  // Start centred on the current value. Later changes come from the wheel itself, so this
  // only needs to run once when the wheel appears.
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = selectedIndex * ITEM_H;
    return () => clearTimeout(timer.current);
  }, []);

  // If the value changes from outside the wheel (e.g. clamped to a minimum), follow it.
  useEffect(() => {
    const el = ref.current;
    if (el && Math.round(el.scrollTop / ITEM_H) !== selectedIndex) {
      el.scrollTo({ top: selectedIndex * ITEM_H, behavior: "smooth" });
    }
  }, [selectedIndex]);

  const handleScroll = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!ref.current) return;
      const index = Math.min(items.length - 1, Math.max(0, Math.round(ref.current.scrollTop / ITEM_H)));
      if (items[index].value !== selected) onSelect(items[index].value);
    }, 90);
  };

  return (
    <div
      ref={ref}
      className="time-wheel"
      onScroll={handleScroll}
      style={{
        height: ITEM_H * VISIBLE,
        width,
        overflowY: "auto",
        scrollSnapType: "y mandatory",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
      }}
    >
      <div style={{ height: PAD }} />
      {items.map((item, i) => (
        <div
          key={item.value}
          onClick={() => ref.current?.scrollTo({ top: i * ITEM_H, behavior: "smooth" })}
          style={{
            height: ITEM_H,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            scrollSnapAlign: "center",
            fontSize: 20,
            cursor: "pointer",
            userSelect: "none",
            color: item.value === selected ? "#111" : "#8c8c99",
            opacity: isDisabled?.(item.value) ? 0.35 : 1,
            fontWeight: item.value === selected ? 600 : 400,
          }}
        >
          {item.label}
        </div>
      ))}
      <div style={{ height: PAD }} />
    </div>
  );
};

const TimeField = ({
  value,
  onChange,
  allowClear = true,
  // Where the wheels start (and what an empty field becomes when opened). 9:00 PM is a
  // typical wedding start time in Saudi Arabia.
  defaultTime = "21:00",
  // Optional "HH:mm": earlier times snap up to it (used when the end falls on the start's day).
  minTime,
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  const isArabic = (i18n.language || "").startsWith("ar");
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const labels = { am: t("timePicker.am"), pm: t("timePicker.pm") };
  const locale = "en-US"; // times use Western digits in both languages (Saudi convention)
  const numFmt = new Intl.NumberFormat(locale, { useGrouping: false });
  const twoFmt = new Intl.NumberFormat(locale, { minimumIntegerDigits: 2, useGrouping: false });
  const clamp = (v) => (minTime && v < minTime ? minTime : v);
  const current = parse(clamp(value || defaultTime)) || parse(defaultTime);
  const emit = (v) => onChange?.(clamp(v));
  const tooEarly = (h12, minute, period) => !!minTime && to24(h12, minute, period) < minTime;

  const handleOpenChange = (next) => {
    setOpen(next);
    // Opening an empty field takes the default straight away, so closing without touching the
    // wheels still leaves a sensible time instead of nothing.
    if (next && !parse(value)) emit(defaultTime);
  };

  const hourItems = HOURS.map((h) => ({ value: h, label: numFmt.format(h) }));
  const minuteItems = MINUTES.map((m) => ({ value: m, label: twoFmt.format(m) }));
  const periodItems = [
    { value: "AM", label: labels.am },
    { value: "PM", label: labels.pm },
  ];

  const content = (
    <div dir="ltr" style={{ position: "relative", display: "flex", justifyContent: "center", gap: 8 }}>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: PAD,
          height: ITEM_H,
          background: "rgba(0,0,0,0.06)",
          borderRadius: 8,
          pointerEvents: "none",
        }}
      />
      <Wheel items={hourItems} selected={current.h12} width={64} isDisabled={(h) => tooEarly(h, current.minute, current.period)} onSelect={(h) => emit(to24(h, current.minute, current.period))} />
      <Wheel items={minuteItems} selected={current.minute} width={64} isDisabled={(m) => tooEarly(current.h12, m, current.period)} onSelect={(m) => emit(to24(current.h12, m, current.period))} />
      <Wheel items={periodItems} selected={current.period} width={isArabic ? 84 : 64} isDisabled={(p) => tooEarly(current.h12, current.minute, p)} onSelect={(p) => emit(to24(current.h12, current.minute, p))} />
    </div>
  );

  const display = formatTime(value, isArabic, labels);
  const trigger = (
    <div className="time-input-wrap">
      <button
        type="button"
        className="native-time-input time-input-display"
        onClick={isMobile ? () => handleOpenChange(!open) : undefined}
        style={{ cursor: "pointer", textAlign: "start", fontFamily: "inherit" }}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...rest}
      >
        <span style={{ color: display ? undefined : "#bfbfbf" }}>{display || t("timePicker.placeholder")}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {allowClear && display && (
            <CloseCircleFilled
              role="button"
              aria-label={t("timePicker.clear")}
              style={{ color: "#bfbfbf" }}
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(undefined);
              }}
            />
          )}
          <ClockCircleOutlined style={{ color: "#8c8c8c" }} />
        </span>
      </button>
    </div>
  );

  return (
    <>
      <style>{`.time-wheel::-webkit-scrollbar { display: none; }`}</style>
      {isMobile ? (
        <>
          {trigger}
          <Drawer
            placement="bottom"
            height="auto"
            open={open}
            onClose={() => setOpen(false)}
            destroyOnClose
            title={t("timePicker.placeholder")}
            styles={{ body: { paddingBottom: 24 } }}
          >
            {content}
          </Drawer>
        </>
      ) : (
        <Popover
          open={open}
          onOpenChange={handleOpenChange}
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          destroyTooltipOnHide
          content={content}
        >
          {trigger}
        </Popover>
      )}
    </>
  );
};

export default TimeField;
